import React, {memo, useCallback, useState, useMemo} from "react"
import {useBackground} from "../../../../hooks/background-class"
import {SettingsPageContainer} from "../../../../components/Settings/SettingsPageContainer"
import {PasswordContainer} from "../../../../components/PasswordContainer"
import {onClickHandler} from "../../../../utils/onclick"
import {Configs} from "../../../../configs"
import {useFilterSelector} from "../../../../hooks/filter-selector"
import {useOrientation} from "../../../../hooks/orientation"
import {MediaFileTag} from "../../../../store/ui/state"
import {useLocalization} from "../../../../hooks/localization"
import {useRPC} from "../../../../hooks/rpc"
import {useMessage} from "../../../../hooks/message"
import iconCancel from "../../../../assets/images/icons/alarm/cancel.png"
import iconOk from "../../../../assets/images/icons/alarm/ok.png"
import iconReturn from "../../../../assets/images/icons/alarm/return.png"
import {useWindowSize} from "../../../../hooks/window-size"
import FileUpload from "./File"
import {useSelector} from "react-redux"
import "./index.scss"

const MyButton = memo(function MyButton({icon, onClick}: {icon?: string; onClick: () => void}) {
    return (
        <div className="button" {...onClickHandler(onClick)}>
            <img src={icon} alt="" />
        </div>
    )
})

export const BackgroundSettingsScreen = memo(function BackgroundSettingsScreen() {
    const [, changeBackground, backgroundStyle] = useBackground()
    const {t} = useLocalization()
    const orientation = useOrientation("horizontal")
    const [rpc] = useRPC()
    const screenRotation = useSelector(state => state.control.workerConfig?.panel?.type)
    const [width, height] = useWindowSize()
    const isPc = useMemo(() => {
        return !((width === 1024 && height === 600) || (height === 1024 && width === 600))
    }, [width, height])

    const backgroundList = useFilterSelector(
        state => state.ui.mediaFileList,
        media => media.type === "background" && media.tags.includes(orientation as MediaFileTag),
        [orientation]
    )

    const [statusModal, setStatusModal] = useState(false)
    const [imagePath, setImagePath] = useState(backgroundList[5]?.id)

    const {failure} = useMessage()
    const deleteMedia = useCallback(() => {
        rpc.deleteMediaFile(imagePath)
            .then(() => changeBackground(backgroundList[5]?.id))
            .then(() => {
                setStatusModal(false)
                window.location.reload()
            })
            .catch(e => {
                setStatusModal(false)
                failure(e)
            })
    }, [imagePath, rpc, backgroundList, changeBackground, failure])

    const changeMedia = useCallback(() => {
        changeBackground(imagePath)
        setStatusModal(false)
    }, [imagePath, changeBackground])

    const onClickUploadWallpeper = useCallback(
        (file: File) => {
            const reader = new FileReader()
            reader.onload = e => {
                if (e.target) {
                    rpc.updateMediaFile("background", e.target.result.toString(), [screenRotation]).then(() => {
                        window.location.reload()
                    })
                }
            }
            reader.readAsDataURL(file)
        },
        [rpc, screenRotation]
    )

    const button = useMemo(
        () => [
            {
                component: isPc && <FileUpload onFileUpload={onClickUploadWallpeper} />
            },
            {
                text: t("default"),
                className: "green",
                onClick: () => changeBackground(backgroundList[5]?.id)
            }
        ],
        [t, changeBackground, onClickUploadWallpeper, isPc, backgroundList]
    )

    return (
        <PasswordContainer requiredRole="default" isFullscreen={true}>
            <SettingsPageContainer
                buttons={button}
                backUrl={"/settings/custom-menu"}
                className={"background-settings"}
                style={backgroundStyle}
            >
                <div className={"background-settings-screen"}>
                    {backgroundList.map(image => (
                        <MyButton
                            key={image.url}
                            icon={
                                image.registered_by_system
                                    ? `${Configs.IMAGE_PREFIX}/bc/images/backgrounds/thumbnail_${image.id}.png`
                                    : `${Configs.IMAGE_PREFIX}/media/${image.id}`
                            }
                            onClick={() => {
                                setStatusModal(true)
                                setImagePath(image.id)
                            }}
                        />
                    ))}
                    <div className={`bg_modal_wrapper ${statusModal ? " " : " _modal-close"}`}>
                        <div className="bg_modal_box">
                            <div className="bg_modal_text">
                                <span>{t("customizations.wallpaper_operations")}</span>
                            </div>
                            <div className="bg_modal_action">
                                <button onClick={() => setStatusModal(false)}>
                                    <img src={iconReturn} alt="" />
                                    {t("customizations.cancelation")}
                                </button>
                                <button {...onClickHandler(changeMedia)}>
                                    <img src={iconOk} alt="" />
                                    {t("customizations.change")}
                                </button>
                                <button {...onClickHandler(deleteMedia)}>
                                    <img src={iconCancel} alt="" />
                                    {t("customizations.delete_wallpaper")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </SettingsPageContainer>
        </PasswordContainer>
    )
})
